<template>
  <v-btn
    elevation="0"
    class="btn"
    :type="type"
    :tag="tag"
    :icon="!!toValue(icon)"
    :loading="loading"
    :class="[
      {
        primary: variant === 'primary',
        accent: variant === 'accent',
        link: variant === 'link',
        'h-auto': type === 'link',
        context: variant === 'context',
        secondary: variant === 'secondary',
        ghost: variant === 'ghost',
        tonal: variant === 'tonal',
      },
      `size-${size}`,
    ]"
  >
    <template #prepend>
      <slot name="prepend" />
      <e-icon v-if="prependIconSrc" :name="prependIconSrc" :size="prependIconSize" />
    </template>

    <e-icon v-if="isShowPrependIconInDefault && prependIconSrc" :name="prependIconSrc" :size="prependIconSize" />
    <e-icon v-if="isShowAppendIconInDefault && appendIconSrc" :name="appendIconSrc" :size="appendIconSize" />

    <slot />

    <template #append>
      <e-icon v-if="appendIconSrc" :name="appendIconSrc" :size="appendIconSize" />
      <slot name="append" />
    </template>
  </v-btn>
</template>

<script setup lang="ts">
import { type Ref, toRef, toValue } from 'vue';
import type { EIconNameType } from '@/core/components/ui/EIcon/EIcon.type';
import { EIcon } from '@/core/components/ui';

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'accent' | 'secondary' | 'link' | 'context' | 'ghost' | 'tonal';
    size?: 'L' | 'M' | 'S';
    type?: 'submit' | 'button' | 'link';
    tag?: string;
    icon?: boolean | Ref<boolean>;
    loading?: boolean;
    prependIconSrc?: EIconNameType;
    prependIconSize?: number | string;
    appendIconSrc?: EIconNameType;
    appendIconSize?: number | string;
  }>(),
  {
    size: 'L',
    type: 'button',
  },
);

defineSlots<{
  default?: (props: unknown) => void;
  prepend?: (props: unknown) => void;
  append?: (props: unknown) => void;
}>();

const isShowPrependIconInDefault = toRef(() => props.icon && props.prependIconSrc);
const isShowAppendIconInDefault = toRef(() => props.icon && props.appendIconSrc);
</script>

<style scoped lang="scss">
.btn {
  z-index: 1;

  min-width: fit-content !important;
  padding: 0 20px !important;

  background-color: transparent;

  &[disabled] {
    pointer-events: none;
  }

  & > *:empty {
    display: none;
  }

  :deep(.v-btn__content) {
    white-space: initial;
  }

  :deep(.v-btn__overlay) {
    display: none;
  }

  :deep(.v-ripple__container) {
    z-index: -1;
  }

  @include md-and-down {
    padding: 0 18px !important;

    font-size: $button-font-size-L_m;
  }

  &:not(.link) {
    :deep(.v-btn__prepend) {
      @include sm-and-down {
        margin-inline: 4px;
      }
    }

    :deep(.v-btn__append) {
      @include sm-and-down {
        margin-inline: 4px;
      }
    }
  }

  &.primary {
    color: $btn-primary-text-color;

    background-color: $btn-primary-bg-color;

    :deep(.v-ripple__animation) {
      --v-theme-overlay-multiplier: 4;
    }

    :deep(.v-ripple__container) {
      color: $btn-primary-pressed-color !important;
    }

    &[disabled],
    &.v-btn--disabled {
      color: $btn-primary-disabled-text-color !important;

      background-color: $btn-primary-disabled-bg-color;
    }

    @include hover {
      background-color: $btn-primary-bg-hover-color;
    }

    @include md-and-down {
      --v-btn-height: 48px;
    }
  }

  &.accent {
    color: $btn-accent-text-color;

    background-color: $btn-accent-bg-color;

    @include hover {
      background-color: $btn-accent-bg-hover-color;
    }
  }

  &.secondary {
    color: $btn-secondary-text-color;

    border: 1px solid $border-color;

    :deep(.v-ripple__animation) {
      --v-theme-overlay-multiplier: 4;
    }

    @include hover {
      color: $btn-secondary-text-hover-color;

      border-color: $btn-secondary-border-hover-color;
    }

    :deep(.v-ripple__container) {
      color: $btn-secondary-pressed-color !important;
    }

    @include md-and-down {
      --v-btn-height: 48px;
    }
  }

  &.link {
    --v-btn-height: 44px;
    padding: 0 6px !important;

    color: $btn-link-text-color;

    background-color: transparent;

    &.size {
      &-M {
        --v-btn-height: 36px;
      }
    }

    &[disabled] {
      color: $text-inactive-color !important;
    }

    :deep(.v-ripple__animation) {
      --v-theme-overlay-multiplier: 0;
    }

    @include hover {
      color: $btn-primary-pressed-color;
    }
  }

  &.h-auto {
    --v-btn-height: auto !important;
  }

  &.context {
    --v-btn-height: 48px;
    padding: 0 12px !important;

    color: $text-secondary-color;

    background-color: transparent;
    border-radius: $border-radius-l;

    &.size {
      &-M {
        --v-btn-height: 40px;
        padding: 0 10px !important;

        border-radius: $border-radius;
      }

      &-S {
        --v-btn-height: 32px;
      }
    }

    &[disabled] {
      color: $btn-ghost-disabled-text-color !important;
    }

    @include hover {
      color: $text-primary-color;

      background-color: $btn-context-hover-bg-color;
    }
  }

  &.ghost {
    --v-btn-height: 52px;

    color: $text-secondary-color;

    background-color: $btn-ghost-bg-color;

    @include hover {
      color: $btn-ghost-text-hover-color;

      background-color: $btn-ghost-hover-bg-color;
    }

    :deep(.v-ripple__animation) {
      --v-theme-overlay-multiplier: 4;
    }

    &:active {
      color: $btn-ghost-text-hover-color;
    }

    &[disabled] {
      color: $btn-ghost-disabled-text-color !important;
    }

    :deep(.v-ripple__container) {
      color: $btn-ghost-pressed-bg-color !important;
    }

    @include md-and-down {
      --v-btn-height: 48px;
    }
  }

  &.size {
    &-M {
      --v-btn-height: 44px;

      font-size: $button-font-size-M;
    }
  }

  &.tonal {
    color: $btn-tonal-text-color;

    background-color: $btn-tonal-bg-color;
  }

  &.v-btn--icon {
    --v-btn-height: 40px;
    padding: 0 !important;

    border-radius: $border-radius-l;

    @include md-and-down {
      --v-btn-height: 36px;
    }

    &.link {
      width: fit-content;
    }

    &.size-M {
      --v-btn-height: 28px;

      border-radius: 12px;
    }

    &.size-S {
      --v-btn-height: 24px;
      border-radius: $input-border-radius;
    }

    &.size-XS {
      --v-btn-height: 12px;
      border-radius: $border-radius;
    }
  }
}
</style>
